import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState } from "react";
import { PickerPatient } from "@/app/[tenant]/(console)/tasks/components/task-inline-editors";
import { CreateTaskModal } from "@/app/[tenant]/(console)/tasks/modals/create-task-modal";
import { separateFiles } from "@/components/chat/attachments";
import { matchesChatFilter } from "@/components/chat/chat-filter";
import { useChatConfigContext } from "@/components/chat/contexts/chat-config-context";
import { useChatFilterContext } from "@/components/chat/contexts/chat-filter-context";
import { useChatData } from "@/components/chat/hooks/use-chat-data";
import { isCallSummary } from "@/contexts/chat-provider";
import { useUser } from "@/contexts/user-provider";
import { useCanWriteTasks } from "@/hooks/use-can-write-tasks";
import { useModule } from "@/hooks/use-module";
import { formatName } from "@/lib/format-data";
import { serializeReference } from "@/lib/notification-reference";
import { cx } from "@/lib/tailwind-utils";
import { toMs } from "@/lib/timestamp";
import { toast } from "@/lib/use-toast";
import { ToolCallSummary } from "@/types/call";
import { CallDetail, ChatEmailSummary, SuggestedMessage } from "@/types/chat";
import { UserType } from "@/types/enums";
import { MessageSummary } from "@/types/message";
import { EmailThreadDialog } from "./email-thread-dialog";
import { Message } from "./message";
import { MessageActionsMenu } from "./message-actions-menu";
import { MessageToolResult } from "./message-tool-result";
import SuggestedContent from "./suggested-content";
import { useDraftTaskFromMessageMutation } from "@/query/comms/chat";

// Type for merged timeline items
type TimelineItem = {
  type: "message";
  data: MessageSummary | CallDetail;
} | {
  type: "email";
  data: ChatEmailSummary;
};
function isEmailItem(item: TimelineItem): item is {
  type: "email";
  data: ChatEmailSummary;
} {
  return item.type === "email";
}
interface MessageListProps {
  messages: (MessageSummary | CallDetail)[];
  emails?: ChatEmailSummary[];
  lastMessage?: MessageSummary | CallDetail | null;
  lastPatientMessage?: MessageSummary | CallDetail | null;
  suggestion?: SuggestedMessage | ToolCallSummary;
  setSuggestion?: (suggestion: SuggestedMessage | ToolCallSummary | undefined) => void;
  onSendMessage?: (messageContent: string, selectedFiles: File[]) => void;
  className?: string;
  showSuggestion?: boolean;
  size?: "sm" | "lg";
}
const MessageList: React.FC<MessageListProps> = t0 => {
  const $ = _c(33);
  const {
    messages,
    emails: t1,
    lastMessage,
    lastPatientMessage,
    suggestion,
    setSuggestion,
    onSendMessage,
    className: t2,
    size: t3,
    showSuggestion: t4
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const emails = t5;
  const className = t2 === undefined ? "" : t2;
  const size = t3 === undefined ? "lg" : t3;
  const showSuggestion = t4 === undefined ? false : t4;
  const {
    chatId
  } = useChatConfigContext();
  const {
    channels,
    searchQuery,
    isFiltered
  } = useChatFilterContext();
  const [selectedThreadId, setSelectedThreadId] = useState();
  const {
    currentUser
  } = useUser();
  const hasChatTasksModule = useModule("CHAT_TASKS");
  const canWriteTasks = useCanWriteTasks();
  const canCreateTask = hasChatTasksModule && canWriteTasks;
  let t6;
  if ($[2] !== chatId) {
    t6 = {
      chatId
    };
    $[2] = chatId;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const {
    chat
  } = useChatData(t6);
  const primaryPatient = chat?.patients.find(p => p.id === chat.primaryPatientId);
  const defaultSelectedPatient = primaryPatient ? {
    id: primaryPatient.id,
    firstName: primaryPatient.firstName,
    lastName: primaryPatient.lastName
  } : undefined;
  const [draftingMessageId, setDraftingMessageId] = useState(null);
  const [taskDraft, setTaskDraft] = useState(null);
  const draftTaskMutation = useDraftTaskFromMessageMutation();
  const handleCreateTask = messageId => {
    if (draftingMessageId) {
      return;
    }
    setDraftingMessageId(messageId);
    const chatReference = serializeReference({
      type: "chat",
      display: primaryPatient ? formatName(primaryPatient.firstName, primaryPatient.lastName) : "Conversation",
      id: chatId
    });
    const openWithoutDraft = () => {
      setTaskDraft({
        title: "",
        description: `From chat: ${chatReference}`
      });
      toast({
        variant: "error",
        title: "Couldn't draft the task. Fill it in below."
      });
    };
    draftTaskMutation.mutate({
      chatId,
      messageId
    }, {
      onSuccess: draft => {
        if (!draft) {
          openWithoutDraft();
          return;
        }
        setTaskDraft({
          title: draft.title,
          description: `${draft.description}\n\nFrom chat: ${chatReference}`
        });
      },
      onError: openWithoutDraft,
      onSettled: () => setDraftingMessageId(null)
    });
  };
  let t7;
  if ($[4] !== channels || $[5] !== emails || $[6] !== isFiltered || $[7] !== messages || $[8] !== searchQuery) {
    const messageItems = messages.map(_temp);
    const emailItems = emails.map(_temp2);
    const allItems = [...messageItems, ...emailItems].filter(item => !isFiltered || matchesChatFilter(item.data, channels, searchQuery));
    t7 = allItems.sort(_temp3);
    $[4] = channels;
    $[5] = emails;
    $[6] = isFiltered;
    $[7] = messages;
    $[8] = searchQuery;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const timeline = t7;
  let t8;
  bb0: {
    if (!isFiltered) {
      t8 = lastMessage;
      break bb0;
    }
    for (let i = timeline.length - 1; i >= 0; i--) {
      const item_0 = timeline[i];
      if (!isEmailItem(item_0) && item_0.data.sender?.type !== UserType.PATIENT) {
        t8 = item_0.data;
        break bb0;
      }
    }
    t8 = null;
  }
  const lastShownMessage = t8;
  const t9 = size === "lg" ? "space-y-4" : "space-y-2";
  let t10;
  if ($[10] !== className || $[11] !== t9) {
    t10 = cx(t9, className);
    $[10] = className;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== isFiltered || $[14] !== timeline.length) {
    t11 = isFiltered && timeline.length === 0 && <p className="p-4 text-center font-light text-foreground-muted">No messages match these filters.</p>;
    $[13] = isFiltered;
    $[14] = timeline.length;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = timeline.map(item_1 => {
    if (isEmailItem(item_1)) {
      return <Message key={`email-${item_1.data.id}`} message={item_1.data} size={size} onClick={() => setSelectedThreadId({
        threadId: item_1.data.threadId,
        messageId: item_1.data.id
      })}><Message.Email /></Message>;
    }
    const message = item_1.data;
    const key = "messageUid" in message && message.messageUid || message.id;
    const isLast = !!(lastShownMessage && message.id === lastShownMessage.id);
    const isLastPatient = !!(lastPatientMessage && message.id === lastPatientMessage.id);
    if (isCallSummary(message)) {
      return <Message key={key} message={message} isLastMessage={isLast} size={size}><Message.Call summary={message.summary} toolCalls={message.toolCalls} audioPath={message.pathToAudio} callId={message.id} size={size} status={message.status} /></Message>;
    }
    const {
      images,
      nonImageFiles
    } = separateFiles(message.files);
    const executedToolCalls = "toolCalls" in message && message.toolCalls?.filter(_temp4) || [];
    const hasExecutedToolCalls = executedToolCalls.length > 0;
    const isLegacyToolResultMsg = hasExecutedToolCalls && message.sender.type === UserType.ASSISTANT && message.content?.startsWith("{");
    if (isLegacyToolResultMsg) {
      return <MessageToolResult key={key} toolCalls={executedToolCalls} size={size} />;
    }
    const isInbound = message.sender.type === UserType.PATIENT;
    const showActions = canCreateTask && !!message.content;
    return <React.Fragment key={key}><Message message={message} isLastMessage={isLast} size={size} actions={showActions ? <MessageActionsMenu align={isInbound ? "start" : "end"} isDrafting={draftingMessageId === message.id} onCreateTask={() => handleCreateTask(message.id)} /> : undefined}>{images.length > 0 && <Message.Images images={images} />}<Message.Text size={size}>{message.content}</Message.Text>{nonImageFiles.length > 0 && <Message.Files files={nonImageFiles} size={size} />}</Message>{hasExecutedToolCalls && <MessageToolResult toolCalls={executedToolCalls} size={size} />}{isLastPatient && showSuggestion && <SuggestedContent key={`${key}-suggestion`} chatId={chatId} suggestion={suggestion} setSuggestion={setSuggestion} onSendMessage={onSendMessage} />}</React.Fragment>;
  });
  const t13 = selectedThreadId?.threadId;
  const t14 = selectedThreadId?.messageId;
  const t15 = !!selectedThreadId;
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setSelectedThreadId(undefined);
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  if ($[17] !== chatId || $[18] !== t13 || $[19] !== t14 || $[20] !== t15) {
    t17 = <EmailThreadDialog threadId={t13} selectedMessageId={t14} chatId={chatId} isOpen={t15} onOpenChange={t16} />;
    $[17] = chatId;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== currentUser?.id || $[23] !== defaultSelectedPatient || $[24] !== setTaskDraft || $[25] !== taskDraft) {
    t18 = taskDraft && <CreateTaskModal open={true} onOpenChange={open => {
      if (!open) {
        setTaskDraft(null);
      }
    }} defaultTitle={taskDraft.title} defaultDescription={taskDraft.description} defaultSelectedPatient={defaultSelectedPatient} defaultAssigneeId={currentUser?.id} />;
    $[22] = currentUser?.id;
    $[23] = defaultSelectedPatient;
    $[24] = setTaskDraft;
    $[25] = taskDraft;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t17 || $[31] !== t18) {
    t19 = <div className={t10}>{t11}{t12}{t17}{t18}</div>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  return t19;
};
export default MessageList;
function _temp(m) {
  return {
    type: "message" as const,
    data: m
  };
}
function _temp2(e) {
  return {
    type: "email" as const,
    data: e
  };
}
function _temp3(a, b) {
  return toMs(a.data.createdDate) - toMs(b.data.createdDate);
}
function _temp4(tc) {
  return tc.result !== null || tc.hasBeenCalled;
}